// =====================================================================
// src/screens/super-admin/lib/users.js
//
// Admin-side READ layer for /users. The rule at /users/{userId} already
// allows read to any signed-in user (line 60 of firestore.rules), so no
// admin-specific rules change was needed for this session — the query
// just works.
//
// Deliberately separate from V1's db.js user helpers (loadUserProfile,
// findOrphanedMemberships, etc.) because those are all single-user reads
// scoped to the current session. Admin needs bulk listing with cursor
// pagination.
//
// COST DISCIPLINE:
//   - Paginated at ADMIN_PAGE_SIZE (25) with cursor-based load-more
//   - No unbounded .get() on the collection
//   - Search filters loaded page client-side, no additional queries
//   - Detail-view reads 0 additional docs (uses list result)
// =====================================================================

import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';

// Default page size — matches BusinessesPage for consistent admin experience.
export const ADMIN_PAGE_SIZE = 25;

// ============================================================
// listUsersForAdmin({ cursor })
// ============================================================
//
// Reads a paginated slice of /users ordered by createdAt DESC (newest
// first — matches admin mental model for "who joined recently" workflows).
//
// This session (Session 3) ships without filter chips. When the AGP
// subsystem lands in a future session, that session will extend this
// helper with a statusFilter param (assigned / not-assigned) matching
// the pattern set by listBusinessesForAdmin.
//
// Args:
//   cursor (DocumentSnapshot | null) — last doc from previous page. Pass
//     null on initial call; pass returned `lastDoc` on subsequent
//     "Load more" clicks.
//
// Returns:
//   {
//     users:   Array<{ id, ...fields }>,
//     lastDoc: DocumentSnapshot | null,
//     hasMore: boolean
//   }
//
// Firestore cost per call: exactly ADMIN_PAGE_SIZE reads (or fewer on
// the last page).
//
// Required indexes: none for this query. orderBy on a single field
// (createdAt) uses Firestore's auto-created single-field index.
//
// Throws: any Firestore error (rules failure, network).
//
export async function listUsersForAdmin({ cursor = null } = {}) {
  const constraints = [orderBy('createdAt', 'desc')];

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  constraints.push(limit(ADMIN_PAGE_SIZE));

  const q = query(collection(db, 'users'), ...constraints);
  const snap = await getDocs(q);

  const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  const hasMore = snap.docs.length === ADMIN_PAGE_SIZE;

  return { users, lastDoc, hasMore };
}
